import { BETROKKEN_STATIONS_QUERY_KEY } from "@/entities/betrokken-stations/constants.ts";
import { DOSSIERS_QUERY_KEY, invalidateOpenDossiersQueries } from "@/entities/dossier";
import { queryClient, useApiClient } from "@/infrastructure/rest";
import { BetrokkenStationEvent, useRealtimeTopic } from "@/infrastructure/rsocket";
import { BetrokkenStationsDto } from "@/shared/types";
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { BetrokkenStation } from "./domain";

function getGetDossierBetrokkenStationsQuery(id: string, apiClient: ReturnType<typeof useApiClient>) {
    return {
        queryKey: [DOSSIERS_QUERY_KEY, id, BETROKKEN_STATIONS_QUERY_KEY],
        queryFn: async () => {
            const data = await apiClient.get<BetrokkenStationsDto[]>(`/dossiers/${id}/betrokken-stations`);
            return data?.map((dto) => BetrokkenStation.fromDto(dto)) ?? [];
        },
    };
}

export function usePrefetchDossierBetrokkenStationsQuery(id: string) {
    const apiClient = useApiClient();
    const query = getGetDossierBetrokkenStationsQuery(id, apiClient);

    return () => queryClient.prefetchQuery({ ...query, staleTime: 60_000 });
}

export function useGetDossierBetrokkenStationsById<T = BetrokkenStation[]>(
    id: string,
    options?: Omit<UseQueryOptions<BetrokkenStation[], Error, T>, "queryFn" | "queryKey">
) {
    const apiClient = useApiClient();
    const query = getGetDossierBetrokkenStationsQuery(id, apiClient);

    return useQuery({
        ...query,
        ...options,
    });
}

interface AddBetrokkenStationsPayload {
    dossierNumber: string;
    stationCodes: string[];
}

/**
 * Adds several stations at once. The backend exposes a single-station endpoint, so each station is
 * mapped to its own POST. R
 */
export function useAddBetrokkenStations() {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ dossierNumber, stationCodes }: AddBetrokkenStationsPayload) => {
            await Promise.all(
                stationCodes.map((stationCode) => apiClient.post(`/dossiers/${dossierNumber}/betrokken-stations`, { stationCode }))
            );
        },
        onSettled: async (_data, _error, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierNumber);
        },
    });
}

interface DeleteBetrokkenStationPayload {
    dossierNumber: string;
    stationCode: string;
}

export function useDeleteBetrokkenStation() {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ dossierNumber, stationCode }: DeleteBetrokkenStationPayload) => {
            await apiClient.delete(`/dossiers/${dossierNumber}/betrokken-stations/${stationCode}`);
        },
        onSuccess: async (_data, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierNumber);
        },
        onError: (error, variables) => {
            console.error(`Error deleting betrokken station ${variables.stationCode} for dossier ${variables.dossierNumber}:`, error);
        },
    });
}

export function useRealtimeBetrokkenStations() {
    const queryClient = useQueryClient();

    useRealtimeTopic<BetrokkenStationEvent>("betrokkenStations", {
        onMessage: (message) => {
            void queryClient.invalidateQueries({
                queryKey: [DOSSIERS_QUERY_KEY, String(message.dossierId), BETROKKEN_STATIONS_QUERY_KEY],
            });
        },
    });
}
